/**
 * Admin -> Edge Fleet -> Install tokens -> Retire silent devices
 * (release 2.1 item 24).
 *
 * An uninstalled laptop, a re-imaged one, one that left the company: each
 * leaves a pod that stops checking in and stays in the fleet as "down" for
 * ever, until someone retires it one by one. This retires, for ONE wave (one
 * install token), every device silent for more than N days - after showing how
 * many that is. The retire call carries the previewed count, and the server
 * refuses it if the number has moved since, so the operator retires exactly
 * what they looked at.
 *
 * Backend:
 *   GET  /v1/edge/install-tokens/:id/silent-devices?days=N
 *   POST /v1/edge/install-tokens/:id/decommission-silent { silentDays, reason, expectedCount }
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  decommissionEdgeSilentDevices,
  previewEdgeSilentDevices,
  type EdgeInstallTokenRow,
} from '@/lib/api';

export function SilentDevicesForm({
  token,
  onDone,
  onCancel,
}: {
  token: EdgeInstallTokenRow;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const [days, setDays] = useState(30);
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof previewEdgeSilentDevices>> | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idBase = `silent-${token.id.slice(0, 8)}`;

  const runPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      setPreview(await previewEdgeSilentDevices(token.id, days));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not count silent devices');
    } finally {
      setBusy(false);
    }
  };

  const retire = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const r = await decommissionEdgeSilentDevices(token.id, {
        silentDays: preview.silentDays,
        reason: reason.trim(),
        expectedCount: preview.count,
      });
      onDone(
        `Decommissioned ${r.decommissioned} device${r.decommissioned === 1 ? '' : 's'} silent for more than ${preview.silentDays} days on token ${token.id.slice(0, 8)}.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not retire the devices');
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-border/40 bg-muted/20 p-3 space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor={`${idBase}-days`} className="text-xs text-muted-foreground block mb-1">
            Offline for more than (days, at least 7)
          </label>
          <input
            id={`${idBase}-days`}
            type="number"
            min={7}
            max={365}
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setPreview(null);
            }}
            className="w-28 rounded border border-border/40 bg-background/40 px-2 py-1.5 text-sm"
          />
        </div>
        <Button size="sm" variant="ghost" onClick={() => void runPreview()} disabled={busy || !(days >= 7)} data-action="disc.adm_edge_fleet.a19">
          {busy && !preview ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
          Count them
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>

      {preview ? (
        preview.count === 0 ? (
          <p className="text-sm text-foreground">
            No device on this token has been silent for more than {preview.silentDays} days.
          </p>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-foreground">
              <strong>{preview.count}</strong> device{preview.count === 1 ? '' : 's'} on this token
              {preview.count === 1 ? ' has' : ' have'} not checked in for more than {preview.silentDays}{' '}
              days. Retiring them removes them from the fleet; a retired device that comes back is
              refused and stops scanning.
            </p>
            <ul className="text-xs text-muted-foreground max-h-32 overflow-y-auto">
              {preview.sample.map((d) => (
                <li key={d.podId}>
                  {d.displayName} ({d.podId.slice(0, 12)}) - last check-in{' '}
                  {d.lastHeartbeatAt ? new Date(d.lastHeartbeatAt).toLocaleString() : 'never'}
                </li>
              ))}
              {preview.count > preview.sample.length ? (
                <li>and {preview.count - preview.sample.length} more</li>
              ) : null}
            </ul>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-48">
                <label htmlFor={`${idBase}-reason`} className="text-xs text-muted-foreground block mb-1">
                  Reason (recorded in the audit log)
                </label>
                <input
                  id={`${idBase}-reason`}
                  type="text"
                  maxLength={250}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Laptops returned to IT after the refresh"
                  className="w-full rounded border border-border/40 bg-background/40 px-2 py-1.5 text-sm"
                />
              </div>
              <Button
                size="sm"
                variant="destructive"
                disabled={busy || reason.trim().length < 3}
                onClick={() => void retire()}
                data-action="disc.adm_edge_fleet.a20"
              >
                {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                Retire {preview.count} device{preview.count === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        )
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
}
